import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, Phone, ShoppingCart, User, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useMall } from "@/context/MallContext";
import { useSettings } from "@/context/SettingsContext";
import { useShopShown } from "@/context/SiteModeContext";
import { mediaUrl } from "@/lib/api";
import { PRINT_SHOPS, TOP_MENU } from "@/lib/nav";
import { ruleFor } from "@/siteMap";
import { useKeepStore } from "@/components/layout/Crumbs";

const isActive = (pathname, to) => {
    const base = to.split("?")[0];
    const root = base.split("/").slice(0, 2).join("/");
    return pathname === base || pathname.startsWith(`${base}/`) || (root !== "/" && pathname.startsWith(root));
};

/** Phase 10 — the shop's own header: a white row with the shop and the register, then the black top menu. */
export default function ShopHeader() {
    const { count, setOpen } = useCart();
    const { user } = useAuth();
    const { shown: store, solo, homeSlug, theme, isHouse } = useMall();
    const settings = useSettings();
    const show = useShopShown();
    const keep = useKeepStore();
    const { pathname } = useLocation();
    const [mobile, setMobile] = useState(false);
    useEffect(() => setMobile(false), [pathname]);

    const menu = TOP_MENU.filter((m) => show(ruleFor(m.to.split("?")[0])));
    const biz = settings?.business || {};
    const name = store?.name || biz.short_name || "13thav";
    const phone = store?.phone || biz.phone || "";
    const phoneRaw = phone.replace(/[^\d+]/g, "");
    const home = store && !solo && store.slug !== homeSlug ? `/stores/${store.slug}` : "/";
    const accent = theme?.primary || "#2563EB";
    const printShops = isHouse && !solo;   // the pitch rides on the house site only; a tenant's site stays theirs

    return (
        <header className="sticky top-0 z-50" data-testid="site-header">
            <div className="border-b border-zinc-200 bg-white">
                <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:gap-4 sm:px-6">
                    <Link to={home} className="flex min-w-0 items-center gap-3" data-testid="logo-link" aria-label={`${name} home`}>
                        {store?.logo ? (
                            <img src={mediaUrl(store.logo)} alt="" className="h-10 w-10 shrink-0 object-contain" data-testid="logo-mark" />
                        ) : (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center bg-ink font-display text-lg font-black text-[#fff]" data-testid="logo-mark">
                                {name.replace(/[^a-z]/gi, "").slice(0, 1) || name.slice(0, 1)}
                            </span>
                        )}
                        <span className="min-w-0">
                            <span className="line-clamp-2 font-display text-sm font-black uppercase leading-[1.05] tracking-normal text-zinc-900 sm:truncate sm:text-xl sm:leading-none sm:tracking-tight" data-testid="shop-name">{name}</span>
                            {store?.city && <span className="mt-1 hidden font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 sm:block" data-testid="shop-city">{store.city}{store.pickup_mode === "locker" ? " · 24-hour locker" : ""}</span>}
                        </span>
                    </Link>

                    <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
                        {phone && (
                            <a href={`tel:${phoneRaw}`} className="hidden items-center gap-2 text-sm font-bold text-zinc-900 hover:underline xl:flex" data-testid="header-phone">
                                <Phone size={15} /> {phone}
                            </a>
                        )}
                        <Link to="/track" className="hidden text-sm font-semibold text-zinc-700 hover:text-zinc-900 md:block" data-testid="header-track">Track order</Link>
                        <Link to={user ? "/account" : "/login"} className="flex h-11 items-center gap-2 border border-zinc-900 px-3 text-sm font-bold uppercase tracking-wide text-zinc-900 hover:bg-ink hover:text-[#fff]" data-testid="account-link">
                            <User size={16} /> <span className="hidden sm:inline">{user ? "Account" : "Login"}</span>
                        </Link>
                        <button type="button" onClick={() => setOpen(true)} data-testid="cart-button"
                            className="relative flex h-11 items-center gap-2 px-4 text-sm font-bold uppercase tracking-wide" style={{ background: accent, color: theme?.on || "#fff" }}>
                            <ShoppingCart size={16} /> <span className="hidden sm:inline">Cart</span>
                            <span className="font-mono" data-testid="cart-count">({count})</span>
                        </button>
                        <button type="button" onClick={() => setMobile(true)} className="flex h-11 w-11 items-center justify-center border border-zinc-900 text-zinc-900 lg:hidden" data-testid="mobile-menu-button" aria-label="Menu">
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </div>

            <nav className="dark-block hidden bg-[#0b0b0b] lg:block" data-testid="top-menu" aria-label="Products">
                <div className="mx-auto flex h-11 max-w-7xl items-stretch px-4 sm:px-6">
                    {menu.map((m) => {
                        const on = isActive(pathname, m.to);
                        return (
                            <Link key={m.key} to={keep(m.to)} data-testid={`menu-${m.key}`} aria-current={on ? "page" : undefined}
                                className={`flex items-center border-b-[3px] px-5 font-display text-[13px] font-black uppercase tracking-[0.16em] transition-colors ${on ? "bg-white/10 text-white" : "border-transparent text-white/80 hover:bg-white/10 hover:text-white"}`}
                                style={on ? { borderColor: accent } : undefined}>
                                {m.label}
                            </Link>
                        );
                    })}
                    {printShops && (
                        <Link to={PRINT_SHOPS.to} data-testid="menu-print-shops" aria-current={isActive(pathname, PRINT_SHOPS.to) ? "page" : undefined}
                            className="ml-auto my-1.5 flex items-center gap-2 px-4 font-display text-[12px] font-black uppercase tracking-[0.16em] transition-transform hover:-translate-y-px"
                            style={{ background: accent, color: theme?.on || "#fff" }}>
                            {PRINT_SHOPS.label} <ArrowRight size={14} />
                        </Link>
                    )}
                </div>
            </nav>

            {mobile && (
                <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setMobile(false)} />
                    <div className="dark-block absolute right-0 top-0 flex h-full w-80 flex-col overflow-y-auto bg-[#0b0b0b] p-5 text-white">
                        <div className="flex items-center justify-between">
                            <span className="font-display text-base font-black uppercase tracking-wide text-white">{name}</span>
                            <button type="button" onClick={() => setMobile(false)} className="flex h-10 w-10 items-center justify-center border border-white/30 text-white" data-testid="mobile-menu-close" aria-label="Close"><X size={18} /></button>
                        </div>
                        <div className="mt-6 flex flex-col">
                            {menu.map((m) => (
                                <Link key={m.key} to={keep(m.to)} data-testid={`mobile-menu-${m.key}`}
                                    className="border-b border-white/15 py-4 font-display text-xl font-black uppercase tracking-wide text-white">{m.label}</Link>
                            ))}
                        </div>
                        {printShops && (
                            <Link to={PRINT_SHOPS.to} data-testid="mobile-menu-print-shops"
                                className="mt-5 flex items-center justify-between px-4 py-3 font-display text-sm font-black uppercase tracking-[0.14em]"
                                style={{ background: accent, color: theme?.on || "#fff" }}>
                                {PRINT_SHOPS.label} <ArrowRight size={16} />
                            </Link>
                        )}
                        <div className="mt-6 flex flex-col gap-3 text-sm text-white/80">
                            <Link to="/track" className="text-white underline" data-testid="mobile-track">Track an order</Link>
                            <Link to={user ? "/account" : "/login"} className="text-white underline" data-testid="mobile-account">{user ? "My account" : "Log in"}</Link>
                            {phone && <a href={`tel:${phoneRaw}`} className="text-white underline">{phone}</a>}
                        </div>
                        <Link to="/cart" className="mt-auto flex h-12 items-center justify-center gap-2 font-bold uppercase tracking-wide" style={{ background: accent, color: theme?.on || "#fff" }} data-testid="mobile-cart">
                            <ShoppingCart size={16} /> Cart ({count})
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
